import type { RecipePath } from './recipePaths'
import { type LabColorInput, toLabColor } from './toLabColor'

export type FrameworkRecipePath =
  | 'framework-crown'
  | 'framework-veneer'
  | 'framework-monolithiccrown'
  | 'framework-implantcrown'
  | 'framework-marylandbridge'
  | 'framework-implantmonolithic'

export const FRAMEWORK_STORED_TYPE_BY_PATH: Record<
  FrameworkRecipePath,
  string
> = {
  'framework-crown': 'framework_crown',
  'framework-veneer': 'framework_veneer',
  'framework-monolithiccrown': 'framework_monolithic_crown',
  'framework-implantcrown': 'framework_implant_crown',
  'framework-marylandbridge': 'framework_maryland_bridge',
  'framework-implantmonolithic': 'framework_implant_monolithic',
}

const ZONED_FRAMEWORK_PATHS: ReadonlySet<string> = new Set([
  'framework-crown',
  'framework-veneer',
  'framework-monolithiccrown',
])

export const isZonedFrameworkPath = (recipePath: string): boolean =>
  ZONED_FRAMEWORK_PATHS.has(recipePath)

// Same URL transform the three framework panels used inline.
export const getFrameworkRecipePath = (
  indication?: string | null,
): RecipePath => {
  if (indication === 'Full Monolithic Crown/Veneer') {
    return 'framework-monolithiccrown'
  }

  const segment = (indication ?? '')
    .replace(/ *\([^)]*\) */g, '')
    .replace(/ /g, '')
    .toLowerCase()
    .split('/')?.[0]

  return `framework-${segment}` as RecipePath
}

export const getFrameworkStoredType = (recipePath: string): string =>
  FRAMEWORK_STORED_TYPE_BY_PATH[recipePath as FrameworkRecipePath] ??
  recipePath.replace(/-/g, '_')

export const getFrameworkStoredTypeFromIndication = (
  indication?: string | null,
): string => getFrameworkStoredType(getFrameworkRecipePath(indication))

type RegionColors = {
  cervical?: LabColorInput | null
  middle?: LabColorInput | null
  incisal?: LabColorInput | null
}

type RegionSpaces = {
  cervical?: unknown
  middle?: unknown
  incisal?: unknown
}

const toSpaceNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined
  const n = Number(String(value).replace(/mm/gi, '').trim())
  return Number.isFinite(n) ? n : undefined
}

const assignLab = (
  payload: Record<string, unknown>,
  key: string,
  color?: LabColorInput | null,
) => {
  const lab = toLabColor(color)
  if (lab) payload[key] = lab
}

const assignSpace = (
  payload: Record<string, unknown>,
  key: string,
  value: unknown,
) => {
  const space = toSpaceNumber(value)
  if (space != null) payload[key] = space
}

export const buildFrameworkPayload = ({
  recipePath,
  frameworkbrand,
  protocol,
  recipeType,
  targetColors,
  prepColors,
  availspace,
}: {
  recipePath: string
  frameworkbrand?: string | null
  protocol?: string
  recipeType?: string | null
  targetColors?: RegionColors
  prepColors?: RegionColors
  availspace?: RegionSpaces
}): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    frameworkbrand: frameworkbrand || '',
    protocol,
  }

  if (isZonedFrameworkPath(recipePath)) {
    assignLab(payload, 'targetcolor_middle', targetColors?.middle)

    if (recipeType === 'Single Zone') {
      assignSpace(payload, 'availspace_middle', availspace?.middle)
      assignLab(payload, 'prepcolor_middle', prepColors?.middle)
      payload.recipe_type = 'single dentin'
    } else {
      assignSpace(payload, 'availspace_cervical', availspace?.cervical)
      assignSpace(payload, 'availspace_middle', availspace?.middle)
      assignSpace(payload, 'availspace_incisal', availspace?.incisal)
      assignLab(payload, 'targetcolor_cervical', targetColors?.cervical)
      assignLab(payload, 'targetcolor_incisal', targetColors?.incisal)
      assignLab(payload, 'prepcolor_cervical', prepColors?.cervical)
      assignLab(payload, 'prepcolor_middle', prepColors?.middle)
      assignLab(payload, 'prepcolor_incisal', prepColors?.incisal)
    }
  } else {
    assignLab(payload, 'targetcolor', targetColors?.middle)
    assignSpace(payload, 'desired_availspace_for_layering', availspace?.middle)
  }

  return payload
}

export type FrameworkRecommendation = {
  name?: string
  substructurename?: string
  thickness_cervical?: number | string | null
  thickness_middle?: number | string | null
  thickness_incisal?: number | string | null
  predictionLAB_cervical?: LabColorInput | null
  predictionLAB_middle?: LabColorInput | null
  predictionLAB_incisal?: LabColorInput | null
  instructions?: string | null
  staining?: boolean
}

export const fromFrameworkRecommendation = (
  item?: FrameworkRecommendation | null,
): FrameworkRecommendation => ({
  name: item?.name ?? item?.substructurename,
  thickness_cervical: item?.thickness_cervical,
  thickness_middle: item?.thickness_middle,
  thickness_incisal: item?.thickness_incisal,
  predictionLAB_cervical: item?.predictionLAB_cervical,
  predictionLAB_middle: item?.predictionLAB_middle,
  predictionLAB_incisal: item?.predictionLAB_incisal,
  instructions: item?.instructions,
  staining: item?.staining,
})

export const getFrameworkAdviceList = (
  output?: {
    frameworkadvice?: FrameworkRecommendation | FrameworkRecommendation[] | null
  } | null,
): FrameworkRecommendation[] => {
  const advice = output?.frameworkadvice
  if (Array.isArray(advice)) return advice
  return advice ? [advice] : []
}

export const getFrameworkBrand = (
  output?: {
    provided_input?: { frameworkbrand?: string; capbrand?: string }
  } | null,
): string | undefined =>
  output?.provided_input?.frameworkbrand ?? output?.provided_input?.capbrand
